import { useMemo, useCallback } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2 } from "lucide-react";
import { DataTable } from "@/components/shared/DataTable";
import type { ProductService } from "@/hooks/useProductsServices";

const UNITS = ["SF", "LF", "EA", "HR", "LS"];
const CUSTOM_PRODUCT = "__custom__";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

export type COType = "addendum" | "deduction" | "backcharge" | "substitution";

export interface COLineItem {
  id: string;
  line_number: number;
  description: string;
  quantity: number;
  unit: string;
  labor_unit_price: number;
  material_unit_price: number;
  unit_price: number;
  product_service_id?: string;
  income_account?: string;
  cogs_account?: string;
  target_sov_line_id?: string;
  vendor_id?: string;
  is_passthrough?: boolean;
}

export interface SovLineOption {
  id: string;
  description: string;
  quantity: number;
  unit: string;
  billed_to_date: number;
  remaining_qty: number;
}

export interface VendorOption {
  id: string;
  name: string;
}

interface Props {
  lines: COLineItem[];
  onChange: (lines: COLineItem[]) => void;
  disabled?: boolean;
  products?: ProductService[];
  coType?: COType;
  sovLines?: SovLineOption[];
  vendors?: VendorOption[];
}

const CATEGORY_ORDER = ["labor", "material", "equipment", "subcontractor"] as const;
const CATEGORY_LABELS: Record<string, string> = {
  labor: "Labor",
  material: "Material",
  equipment: "Equipment",
  subcontractor: "Subcontractor",
};

function groupProducts(products: ProductService[]) {
  const groups: Record<string, ProductService[]> = {};
  for (const p of products) {
    if (!p.is_active || p.status !== "active") continue;
    const cat = p.category || "other";
    (groups[cat] ??= []).push(p);
  }
  return groups;
}

export const COLineItemsForm = ({ lines, onChange, disabled, products, coType = "addendum", sovLines, vendors }: Props) => {
  const groupedProducts = useMemo(() => (products ? groupProducts(products) : {}), [products]);

  const isDeductionMode = coType === "deduction" || coType === "backcharge";

  const addLine = () => {
    const next: COLineItem = {
      id: typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      line_number: lines.length + 1,
      description: "",
      quantity: isDeductionMode ? -1 : 1,
      unit: "SF",
      labor_unit_price: 0,
      material_unit_price: 0,
      unit_price: 0,
      is_passthrough: false,
    };
    onChange([...lines, next]);
  };

  const updateLine = useCallback(
    (id: string, field: keyof COLineItem, value: string | number | boolean) => {
      onChange(
        lines.map((l) => {
          if (l.id !== id) return l;
          const updated = { ...l, [field]: value };
          if (field === "labor_unit_price" || field === "material_unit_price") {
            updated.unit_price = Number(updated.labor_unit_price) + Number(updated.material_unit_price);
          }
          return updated;
        })
      );
    },
    [lines, onChange]
  );

  const applyProduct = useCallback(
    (lineId: string, productId: string) => {
      if (productId === CUSTOM_PRODUCT) {
        onChange(
          lines.map((l) =>
            l.id === lineId
              ? { ...l, product_service_id: undefined, income_account: undefined, cogs_account: undefined }
              : l
          )
        );
        return;
      }
      const product = products?.find((p) => p.id === productId);
      if (!product) return;

      const isLabor = product.category === "labor" || product.category === "subcontractor";
      const laborRate = isLabor ? product.default_cost_price : 0;
      const materialRate = !isLabor ? product.default_cost_price : 0;

      onChange(
        lines.map((l) => {
          if (l.id !== lineId) return l;
          return {
            ...l,
            product_service_id: product.id,
            description: product.name,
            unit: product.unit || l.unit,
            labor_unit_price: laborRate,
            material_unit_price: materialRate,
            unit_price: laborRate + materialRate,
            income_account: product.income_account ?? undefined,
            cogs_account: product.cogs_account ?? undefined,
          };
        })
      );
    },
    [lines, onChange, products]
  );

  const removeLine = (id: string) => {
    onChange(
      lines
        .filter((l) => l.id !== id)
        .map((l, i) => ({ ...l, line_number: i + 1 }))
    );
  };

  const total = lines.reduce((sum, l) => sum + l.quantity * l.unit_price, 0);

  const hasProducts = products && products.length > 0;

  const columns = useMemo<ColumnDef<COLineItem, any>[]>(
    () => [
      {
        accessorKey: "line_number",
        header: "#",
        size: 50,
        cell: ({ row }) => (
          <span className="text-muted-foreground">{row.original.line_number}</span>
        ),
      },
      ...(hasProducts
        ? [
            {
              id: "product",
              header: "Product",
              size: 160,
              cell: ({ row }: any) => (
                <Select
                  disabled={disabled}
                  value={row.original.product_service_id || CUSTOM_PRODUCT}
                  onValueChange={(v) => applyProduct(row.original.id, v)}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Custom" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={CUSTOM_PRODUCT}>Custom</SelectItem>
                    {CATEGORY_ORDER.map((cat) => {
                      const items = groupedProducts[cat];
                      if (!items?.length) return null;
                      return (
                        <SelectGroup key={cat}>
                          <SelectLabel>{CATEGORY_LABELS[cat] ?? cat}</SelectLabel>
                          {items.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.code} — {p.name}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      );
                    })}
                  </SelectContent>
                </Select>
              ),
            } as ColumnDef<COLineItem, any>,
          ]
        : []),
      {
        accessorKey: "description",
        header: "Description",
        size: 200,
        cell: ({ row }) => (
          <Input
            disabled={disabled}
            value={row.original.description}
            onChange={(e) => updateLine(row.original.id, "description", e.target.value)}
            placeholder="Scope description"
            className="h-8 text-sm"
          />
        ),
      },
      {
        id: "is_passthrough",
        header: "Pass-Through",
        size: 90,
        cell: ({ row }) => (
          <div className="flex items-center justify-center">
            <Checkbox
              disabled={disabled}
              checked={row.original.is_passthrough ?? false}
              onCheckedChange={(checked) =>
                updateLine(row.original.id, "is_passthrough" as any, !!checked)
              }
            />
          </div>
        ),
      },
      // SOV Line picker for deductions
      ...((coType === "deduction" || coType === "substitution") && sovLines && sovLines.length > 0
        ? [
            {
              id: "target_sov",
              header: "SOV Line",
              size: 180,
              cell: ({ row }: any) => (
                <Select
                  disabled={disabled}
                  value={row.original.target_sov_line_id || ""}
                  onValueChange={(v) => {
                    const sov = sovLines.find((s) => s.id === v);
                    onChange(
                      lines.map((l) => {
                        if (l.id !== row.original.id) return l;
                        return {
                          ...l,
                          target_sov_line_id: v,
                          description: sov?.description || l.description,
                          unit: sov?.unit || l.unit,
                          quantity: -(sov?.remaining_qty || 1),
                        };
                      })
                    );
                  }}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Select SOV line" />
                  </SelectTrigger>
                  <SelectContent>
                    {sovLines.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.description} ({s.remaining_qty} {s.unit} remaining)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ),
            } as ColumnDef<COLineItem, any>,
          ]
        : []),
      // Vendor picker for backcharges
      ...(coType === "backcharge" && vendors && vendors.length > 0
        ? [
            {
              id: "vendor",
              header: "Vendor",
              size: 160,
              cell: ({ row }: any) => (
                <Select
                  disabled={disabled}
                  value={row.original.vendor_id || ""}
                  onValueChange={(v) => updateLine(row.original.id, "vendor_id" as any, v)}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ),
            } as ColumnDef<COLineItem, any>,
          ]
        : []),
      {
        accessorKey: "quantity",
        header: isDeductionMode ? "Deduct Qty" : "Qty",
        size: 80,
        cell: ({ row }) => {
          const sovLine = sovLines?.find((s) => s.id === row.original.target_sov_line_id);
          const maxDeduct = sovLine?.remaining_qty;
          return (
            <Input
              disabled={disabled}
              type="number"
              step="0.01"
              key={`qty-${row.original.id}-${row.original.product_service_id ?? "c"}`}
              defaultValue={row.original.quantity}
              onBlur={(e) => {
                let val = Number(e.target.value) || 0;
                // For deductions with a target SOV line, validate against remaining qty
                if (isDeductionMode && maxDeduct !== undefined && Math.abs(val) > maxDeduct) {
                  val = -maxDeduct;
                  e.target.value = String(val);
                }
                updateLine(row.original.id, "quantity", val);
              }}
              className="h-8 text-sm text-right"
            />
          );
        },
      },
      {
        accessorKey: "unit",
        header: "Unit",
        size: 80,
        cell: ({ row }) => (
          <Select
            disabled={disabled}
            value={row.original.unit}
            onValueChange={(v) => updateLine(row.original.id, "unit", v)}
          >
            <SelectTrigger className="h-8 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {UNITS.map((u) => (
                <SelectItem key={u} value={u}>
                  {u}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ),
      },
      {
        accessorKey: "labor_unit_price",
        header: "Labor Rate",
        size: 100,
        cell: ({ row }) => (
          <Input
            disabled={disabled}
            type="number"
            min={0}
            step="0.01"
            key={`labor-${row.original.id}-${row.original.product_service_id ?? "c"}`}
            defaultValue={row.original.labor_unit_price}
            onBlur={(e) => updateLine(row.original.id, "labor_unit_price", Number(e.target.value) || 0)}
            className="h-8 text-sm text-right"
          />
        ),
      },
      {
        accessorKey: "material_unit_price",
        header: "Material Rate",
        size: 100,
        cell: ({ row }) => (
          <Input
            disabled={disabled}
            type="number"
            min={0}
            step="0.01"
            key={`mat-${row.original.id}-${row.original.product_service_id ?? "c"}`}
            defaultValue={row.original.material_unit_price}
            onBlur={(e) => updateLine(row.original.id, "material_unit_price", Number(e.target.value) || 0)}
            className="h-8 text-sm text-right"
          />
        ),
      },
      {
        accessorKey: "unit_price",
        header: "Unit Price",
        size: 100,
        enableSorting: true,
        cell: ({ row }) => (
          <span className="text-right block font-mono text-sm">
            {fmt(row.original.unit_price)}
          </span>
        ),
      },
      {
        id: "amount",
        header: "Amount",
        size: 110,
        accessorFn: (row) => row.quantity * row.unit_price,
        cell: ({ row }) => (
          <span className="text-right block font-mono text-sm font-medium">
            {fmt(row.original.quantity * row.original.unit_price)}
          </span>
        ),
      },
      ...(!disabled
        ? [
            {
              id: "actions",
              header: "",
              size: 40,
              enableSorting: false,
              cell: ({ row }: any) => (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                  onClick={() => removeLine(row.original.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              ),
            } as ColumnDef<COLineItem, any>,
          ]
        : []),
    ],
    [disabled, updateLine, removeLine, hasProducts, applyProduct, groupedProducts, coType, sovLines, vendors, isDeductionMode, lines, onChange]
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">Line Items</h4>
        {!disabled && (
          <Button type="button" size="sm" variant="outline" onClick={addLine}>
            <Plus className="mr-1 h-3.5 w-3.5" />
            Add Line
          </Button>
        )}
      </div>

      {lines.length === 0 ? (
        <div className="text-center py-6 border rounded-md bg-muted/20">
          <p className="text-sm text-muted-foreground mb-2">No line items yet</p>
          {!disabled && (
            <Button type="button" size="sm" variant="outline" onClick={addLine}>
              Add your first line item
            </Button>
          )}
        </div>
      ) : (
        <div>
          <DataTable<COLineItem>
            tableId="co-line-items"
            columns={columns}
            data={lines}
            enableFiltering={false}
            enableColumnToggle={false}
            enableSorting={true}
            enableResize={true}
            enableReorder={true}
            emptyMessage="No line items"
          />
          <div className="flex justify-between items-center px-4 py-2 bg-muted/30 border border-t-0 rounded-b-md">
            <span className="text-sm font-semibold">Total</span>
            <span className="text-lg font-bold font-mono">{fmt(total)}</span>
          </div>
        </div>
      )}
    </div>
  );
};
